<template>
  <loader v-if="fetchResumeItemPending"/>
  <v-container v-else>
    <v-row>
      <v-col :cols="12">
        <div class="d-flex align-center">
          <v-btn text icon @click="() => $router.go(-1)">
            <v-icon>chevron_left</v-icon>
          </v-btn>
          <h1>
            {{ form.id > 0 ? $t('texts.editLanguage') : $t('texts.addLanguage') }}
          </h1>
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-card>
          <v-card-text>
            <v-form>
              <v-container grid-list-xl class="pa-0">
                <v-row>
                  <v-col>
                    <v-autocomplete
                      v-model="form.language"
                      :items="selectableLanguageItems"
                      :label="$tc('texts.language.label', 1)"
                      item-text="name"
                      item-value="key"
                      required
                      :error-messages="errors.collect('language')"
                      v-validate="'required'"
                      data-vv-name="language"
                      :data-vv-as="$tc('texts.language.label', 1)"
                      :readonly="!!form.id"
                    />
                    <v-autocomplete
                      v-model="form.proficiency"
                      :items="proficiencyItems"
                      :label="$t('texts.proficiency.label')"
                      item-text="title"
                      item-value="value"
                      required
                      :error-messages="errors.collect('proficiency')"
                      v-validate="'required'"
                      data-vv-name="proficiency"
                      :data-vv-as="$t('texts.proficiency.label')"
                    />
                  </v-col>
                </v-row>
                <v-divider/>
                <v-row>
                  <v-col :cols="12">
                    <file-upload
                      :documents="form.documents"
                      @interface="editedDocuments"
                      :uploadType="`languagesUpload-${form.id}`"
                    />
                  </v-col>
                </v-row>
              </v-container>
            </v-form>
          </v-card-text>
          <v-divider/>
          <v-card-actions class="py-3 px-5">
            <v-btn
              color="red darken-2"
              v-if="form.id > 0"
              class="white--text"
              @click="deleteItem()"
              :disabled="deleteResumeItemPending || storeResumeItemPending"
              :loading="deleteResumeItemPending"
            >
              {{ $t('texts.deleteEntry') }}
              <v-icon right class="white--text">delete</v-icon>
            </v-btn>
            <v-spacer/>
            <v-btn
              color="primary" class="white--text"
              @click="handleFormSubmit()"
              :disabled="deleteResumeItemPending || storeResumeItemPending"
              :loading="storeResumeItemPending"
            >
              {{ form.id > 0 ? $t('texts.saveChanges') : $t('texts.save') }}
              <v-icon right class="white--text">check_circle</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <confirmation-dialog ref="confirm"/>
  </v-container>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import FileUpload from '@/components/_organisms/FileUpload.vue';
import ConfirmationDialog from '@/components/_organisms/ConfirmationDialog.vue';

const { mapState: mapEmployeeState, mapActions: mapEmployeeActions } = createNamespacedHelpers('employee');
const { mapActions: mapMessageActions } = createNamespacedHelpers('messages');

export default {
  name: 'EditResumeLanguage',
  $_veeValidate: {
    validator: 'new',
  },
  components: {
    FileUpload,
    ConfirmationDialog,
  },
  data() {
    return {
      form: {
        id: null,
        language: '',
        proficiency: '',
        documents: [],
      },
      storedLanguages: [],
    };
  },
  created() {
    this.reset();
    if (this.$route.params.item) {
      Object.assign(this.form, this.$route.params.item);
    } else if (this.$route.params.id) {
      const payload = {
        type: this.$route.meta.resumeType,
        id: this.$route.params.id,
      };
      this.fetchResumeItem(payload).then((response) => {
        Object.assign(this.form, response.data.data);
      });
    }
  },
  computed: {
    ...mapEmployeeState([
      'resume',
      'fetchResumeItemPending',
      'storeResumeItemPending',
      'deleteResumeItemPending',
    ]),
    languageItems() {
      const items = [
        { key: 'de', name: this.$i18n.t('texts.language.de') },
        { key: 'en', name: this.$i18n.t('texts.language.en') },
        { key: 'es', name: this.$i18n.t('texts.language.es') },
        { key: 'zh', name: this.$i18n.t('texts.language.zh') },
        { key: 'pt', name: this.$i18n.t('texts.language.pt') },
        { key: 'fr', name: this.$i18n.t('texts.language.fr') },
        { key: 'it', name: this.$i18n.t('texts.language.it') },
        { key: 'ru', name: this.$i18n.t('texts.language.ru') },
        { key: 'nl', name: this.$i18n.t('texts.language.nl') },
        { key: 'pl', name: this.$i18n.t('texts.language.pl') },
        { key: 'ro', name: this.$i18n.t('texts.language.ro') },
        { key: 'no', name: this.$i18n.t('texts.language.no') },
        { key: 'cs', name: this.$i18n.t('texts.language.cs') },
        { key: 'el', name: this.$i18n.t('texts.language.el') },
        { key: 'tr', name: this.$i18n.t('texts.language.tr') },
        { key: 'da', name: this.$i18n.t('texts.language.da') },
        { key: 'ar', name: this.$i18n.t('texts.language.ar') },
        { key: 'he', name: this.$i18n.t('texts.language.he') },
        { key: 'ja', name: this.$i18n.t('texts.language.ja') },
        { key: 'ko', name: this.$i18n.t('texts.language.ko') },
        { key: 'sv', name: this.$i18n.t('texts.language.sv') },
        { key: 'fi', name: this.$i18n.t('texts.language.fi') },
        { key: 'hu', name: this.$i18n.t('texts.language.hu') },
      ];
      return items;
    },
    selectableLanguageItems() {
      const items = [...this.languageItems];
      if (this.resume.languages.length > 0) {
        const usedLanguages = this.resume.languages.map((item) => item.language);
        return items.filter((item) => !usedLanguages.includes(item.key) || this.form.language);
      }
      return items;
    },
    proficiencyItems() {
      const items = [
        { value: 1, title: this.$i18n.t('texts.proficiency.1') },
        { value: 2, title: this.$i18n.t('texts.proficiency.2') },
        { value: 3, title: this.$i18n.t('texts.proficiency.3') },
        { value: 4, title: this.$i18n.t('texts.proficiency.4') },
      ];
      return items;
    },
    selectedLanguage() {
      const language = this.languageItems.find((item) => item.key === this.form.language);
      return this.form.name;
    },
  },
  methods: {
    ...mapEmployeeActions([
      'fetchResumeItem',
      'storeResumeItem',
      'deleteResumeItem',
    ]),
    ...mapMessageActions([
      'showMessageValidateError',
    ]),
    reset() {
      this.form = {
        id: '',
        language: '',
        proficiency: '',
        documents: [],
      };
      this.$validator.reset();
    },
    editedDocuments(editedDocuments) {
      this.form.documents = editedDocuments;
    },
    handleFormSubmit() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          const payload = {
            type: 'languages',
            item: this.form,
            successMessageKey: this.form.id ? 'alert.updateResumeLanguagesSuccessful' : 'alert.addResumeLanguagesSuccessful',
            errorMessageKey: this.form.id ? 'alert.updateResumeLanguagesError' : 'alert.addResumeLanguagesError',
          };
          this.storeResumeItem(payload)
            .then(() => {
              this.$router.go(-1);
              this.$eventBus.$emit('clear-file-upload');
            });
        } else {
          this.showMessageValidateError();
        }
      });
    },
    async deleteItem() {
      const confirmDialog = async () => this.$refs.confirm.open({
        title: 'deleteEntry',
        message: 'deleteEntryQuestion',
        confirm: 'delete',
        reject: 'cancel',
      });
      if (await confirmDialog()) {
        const payload = {
          type: 'languages',
          item: this.form,
          successMessageKey: 'alert.deleteResumeLanguagesSuccessful',
          errorMessageKey: 'alert.deleteResumeLanguagesError',
        };
        this.deleteResumeItem(payload)
          .then(() => {
            this.$router.go(-1);
          });
      }
    },
  },
};
</script>
